import { useRuntimeConfig } from "src/services/RuntimeConfig"
import { defineBoot } from "#q-app/wrappers"
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"
import axios from "axios"
import { date as DateFormatter } from "quasar"

import type {
    BuzzSentiment,
    CountriesInner,
    EntityType,
    ListsInner,
    Message,
    PersonV6,
    QuotesForTitlesQuotesInner,
    SearchMessages,
    SearchMessagesStatisticsTop10,
    SearchMessagesStatisticsTop10Entities,
    TitleInformation,
} from "@stockpulse/typescript-axios"
import { differenceInMilliseconds } from "date-fns"
import type { RouteThrottleConfig } from "src/services/AxiosRequestManager"
import { AxiosRequestManager, CancellationPolicy } from "src/services/AxiosRequestManager"
import type {
    AssetEntityType,
    ExchangeData,
    InventorySector,
    InventoryTitle,
    KeyEvent,
    UiPreferences,
    UserOpts,
} from "stores/inventory-store"
import type { ReportModel } from "src/helper/ReportHelper"
import { DEFAULT_REPORT_MODELS, getReportModel, ReportModelType } from "src/helper/ReportHelper"
import type { AuthorHistoryData } from "stores/author-view-store"
import type { SearchMessagesStatisticsAuthorAnalytics } from "@stockpulse/typescript-axios/types/search-messages-statistics-author-analytics"
import type { MessageLanguage, MessageSentiment, MessageType } from "src/helper/MessageHelper"
import type { Title } from "src/types/Title"
import type { TitleChartData } from "src/types/TitleChartData"
import type { TitleHighlight } from "src/types/TitleHighlight"
import type { StandardTriggersInner } from "@stockpulse/typescript-axios/types/standard-triggers-inner"
import type { EarningsTitlesInner } from "@stockpulse/typescript-axios/types/earnings-titles-inner"
import type { StreamPost } from "src/types/StreamPosts"
import type { AsyncData } from "components/models"
import type {
    BacktestRequest,
    BacktestResult,
    BacktestSignal,
    PolicySettings,
    SignalInput,
    SignalOrderTemplate,
    StrikePriceInterval,
    Trade,
} from "src/types/Backtest"
import { isInstanceOfOptionTemplate } from "src/types/Backtest"

const IMAGE_PROXY_DOMAIN = "https://img-app.stockpulse.ai"

export interface SearchResultEntity {
    entity_id: number
    name: string
    type: string
    subtype?: string
    score: number
}

export type SolrEntitySearchResult = {
    num_found: number
    solr_query_ms: number
    entities: SearchResultEntity[]
}

export type SolrEntitiesSearchResult = {
    [p: string]: SolrEntitySearchResult
}

export enum Level {
    FULL = "full",
    NONE = "none",
    PARTIAL = "partial",
}

export interface TurnstileOptions {
    "sitekey": string
    "theme": "light" | "dark" | "auto"
    "language": string
    "size": string

    callback(token: string): void

    "action": string
    "appearance": string

    "error-callback"(code: string): void

    "unsupported-callback"(): void
}

declare global {
    interface Window {
        turnstile: {
            reset(): void
            remove(widgetId: string): void
            render(htmlRef: HTMLDivElement, options: TurnstileOptions): string
        }
        ["cfTurnstileOnLoad"]: unknown
    }
}

declare module "@vue/runtime-core" {
    interface ComponentCustomProperties {
        $axios: AxiosInstance
        $api: AxiosInstance
    }
}

export interface FilterSetting {
    field: string
    value: string | string[] | number | number[]
    comparator: "gt" | "lt" | "gte" | "lte" | "eq"
}

export type FilterSettingsList = FilterSetting[]

export interface TitleFilter {
    asset: number[]
    list: number[]
    sector: number[]
    exchange: number[]
    region: string[]
    country: string[]
    type: EntityType[]
}

export type ViewFilter = TitleFilter & { keyEvent: number[] }

export type ViewFilterKeys = keyof ViewFilter

export interface MessageFilterEntities {
    messageType: MessageType[]
    messageLanguage: MessageLanguage[]
    authorType: string[]
    keyEvent: KeyEvent[]
    messageAsset: InventoryTitle[]
    messageSentiment: MessageSentiment[]
}

export interface ViewFilterEntities {
    list: ListsInner[]
    sector: InventorySector[]
    exchange: ExchangeData[]
    country: CountriesInner[]
    region?: string[]
    type: AssetEntityType[]
    keyEvent?: KeyEvent[]
    asset?: InventoryTitle[]
}

export interface SolrTitleFilters {
    filters: FilterSetting[]
    add_to_search: boolean
    restrict_response: boolean
}

export interface HistoryChartData {
    history: Array<{ t: number; Buzz: number; Sentiment: number }>
    reportModel: ReportModel
}

export interface HistoryQuoteData {
    history: QuotesForTitlesQuotesInner[]
    reportModel: ReportModel
}

export interface TitleFromFilter {
    id: number
    country?: string
    listed_at?: number[]
    sector?: number
    n?: string
}

export interface TitleSummary {
    summary: string
    messages: Message[]
    highlights: TitleHighlight[]
    publishDate?: number
}

export interface MessagesSummary {
    executive_summary?: string
    messages?: Message[]
    executive_summary_highlights?: TitleHighlight[]
}

export enum MessagesCore {
    Messages = "messages",
    Messages24h = "messages24h",
}

export interface TitlesBuzzSentiment {
    [key: number]: BuzzSentiment
}

// TODO_NEXT: Update API Typedefinitions
declare module "@stockpulse/typescript-axios" {
    interface SearchMessagesStatisticsTop10Titles {
        [key: number]: number
    }

    interface SearchMessagesStatisticsTop10AuthorsInner {
        [key: number]: number
    }

    interface SearchMessagesStatisticsTop10KeyEvents {
        [key: number]: number
    }

    interface SearchMessagesStatisticsCounts {
        pos?: number
        neg?: number
        neu?: number
    }

    interface SearchMessagesStatisticsHistoryInner {
        pos: number
        neg: number
        neu: number
    }

    interface SearchMessages {
        executive_summary?: string
        topics_list?: string[]
    }

    interface AuthorStatistics {
        history: AuthorHistoryData[]
    }

    interface TitleInformation {
        url: string
        next_earnings?: string
    }

    interface ListsInner {
        icon: string
    }

    interface ListsInner {
        country: string
    }

    interface TitleInventoryInner {
        buzz?: number
        sentiment?: number
        b_o?: number
        s_o?: number
        t: number
    }

    interface TitleMessages {
        [key: number]: { total: number; pos: number; neg: number; sentiment: number }
    }

    interface SearchMessagesStatistics {
        oldest_message?: Message
        youngest_message?: Message
        average_sentiment?: number
        counts?: SearchMessagesStatisticsCounts
        top10?: SearchMessagesStatisticsTop10
        history?: SearchMessagesStatisticsHistoryInner[]
        author_analytics?: SearchMessagesStatisticsAuthorAnalytics
        title_messages?: TitleMessages
    }

    interface HistoricBuzzSentiment {
        titles?: unknown[]
    }
}

// TODO_NEXT: Update API Typedefinitions
declare module "@stockpulse/typescript-axios/types" {
    interface StandardTriggersInner {
        icon?: string
        release_date?: string
    }
}

const runtimeConfig = useRuntimeConfig()

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
const api = axios.create({
    baseURL: runtimeConfig.API_BASE_URL,
    headers: {
        "x-app-version": process.env.APP_VERSION_NAME,
        // TODO_NEXT: this prevents caching to avoid Edge cross-origin cache problem
        // 1. open app and login
        // 2. open app from another origin
        // 3. some requests will fail due to cross-origin caching
        "Cache-Control": "private, no-cache, no-store, must-revalidate",
    },
})

const routeThrottleConfigs: RouteThrottleConfig[] = [
    { route: "/v6", concurrency: 4 },
    { route: "", concurrency: 10 },
]

const axiosRequestManager = new AxiosRequestManager(routeThrottleConfigs)

export default defineBoot(({ app }) => {
    // for use inside Vue files (Options API) through this.$axios and this.$api

    app.config.globalProperties.$axios = axios
    // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
    //       so you won't necessarily have to import axios in each vue file

    app.config.globalProperties.$api = api
    // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
    //       so you can easily perform requests against your app's API

    api.interceptors.request.use((config: AxiosRequestConfig) => axiosRequestManager.interceptRequest(config))

    api.interceptors.response.use(
        (response: AxiosResponse) => axiosRequestManager.interceptResponse(response),
        (error) => axiosRequestManager.interceptResponseError(error)
    )
})

async function fetchInfosForTitles(
    titleIds: number[],
    cancellationPolicy?: CancellationPolicy
): Promise<TitleInformation[]> {
    if (!titleIds.length) {
        return []
    }
    const response = await api.get(`/v6/titles/${titleIds.join(",")}/info`, { cancellationPolicy })
    return Array.isArray(response.data) ? response.data : response.data ? [response.data] : []
}

async function fetchStatistics(
    startDate: Date,
    endDate: Date,
    titleIds: number[],
    sourceFilter: string[],
    keyEventIds?: number[],
    query?: string,
    lang?: string[],
    type?: string[],
    topLimit?: number,
    authors?: Array<{ source: string; name: string }>,
    titleFilters?: SolrTitleFilters,
    sentimentFilter?: string[],
    includeStats: boolean = true,
    includeTopics: boolean = false,
    includeExecSummary: boolean = false,
    queryExpansion: boolean = false,
    offset?: number,
    score?: number
): Promise<SearchMessages | undefined> {
    const body = {
        startDate,
        endDate,
        include_stats: includeStats,
        core: getCoreToUse(startDate),
        titles: titleIds,
        source: sourceFilter,
        add_icon: true,
        key_events: keyEventIds,
        query,
        lang,
        type: type?.flatMap((str) => (str.includes(",") ? str.split(",") : str)),
        top_limit: topLimit,
        authors,
        title_filters: titleFilters,
        sentiment: sentimentFilter ? getSentimentSolrFilter(sentimentFilter) : undefined,
        include_exec_summary: includeExecSummary,
        include_topics_list: includeTopics,
        query_expansion: queryExpansion,
        offset,
        score,
    }
    const response = await api.post("/v6/messages/search", body)
    return response.data
}

function getSentimentSolrFilter(sentimentFilter: string[]): string | undefined {
    if (sentimentFilter.length === 3) {
        return undefined
    }

    const sentimentQueries: Record<string, string> = {
        positive: "{0 TO *}",
        negative: "{* TO 0}",
        neutral: "0",
    }

    const queryParts = sentimentFilter
        .filter((sentiment) => sentiment in sentimentQueries)
        .map((sentiment) => sentimentQueries[sentiment])

    return queryParts.length > 0 ? queryParts.join(" OR sentiment:") : undefined
}

export interface AuthorFetch {
    source: string
    name: string
}

async function fetchSolrMessages(
    startDate: Date,
    endDate: Date,
    titleIds: number[],
    sourceFilter: string,
    keyEventIds?: number[],
    type?: string[],
    query?: string,
    lang?: string[],
    score?: number,
    authors?: AuthorFetch[],
    primaryTitle?: number,
    limit?: number,
    sentimentFilter?: string[],
    queryExpansion?: boolean
): Promise<Message[]> {
    const body = {
        startDate,
        endDate,
        core: getCoreToUse(startDate),
        titles: titleIds,
        source: sourceFilter,
        add_icon: true,
        key_events: keyEventIds,
        type: type?.flatMap((str) => (str.includes(",") ? str.split(",") : str)),
        query,
        lang,
        score,
        authors,
        primary_title: primaryTitle,
        sentiment: sentimentFilter ? getSentimentSolrFilter(sentimentFilter) : undefined,
        query_expansion: queryExpansion,
    }

    const url = "/v6/messages/search" + (limit !== undefined ? `?limit=${limit}` : "")
    const response = await api.post(url, body)
    return response.data?.messages || []
}

interface SearchMessagesWithTopics {
    /**
     *
     * @memberof SearchMessages
     */
    topics_list: string[]
}

async function fetchTopicsList(
    startDate: Date,
    endDate: Date,
    titleIds: number[],
    sourceFilter: string,
    keyEventIds?: number[]
): Promise<SearchMessagesWithTopics | undefined> {
    const body = {
        startDate,
        endDate,
        include_topics_list: true,
        core: getCoreToUse(startDate),
        titles: titleIds,
        source: sourceFilter,
        key_events: keyEventIds,
    }

    const response = await api.post("/v6/messages/search", body, {
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data
}

async function fetchSummary(
    startDate: Date,
    endDate: Date,
    titleIds: number[],
    keyEventIds?: number[]
): Promise<TitleSummary> {
    const body = {
        startDate,
        endDate,
        core: getCoreToUse(startDate),
        titles: titleIds,
        limit: 20,
        type: "news",
        include_stats: false,
        include_exec_summary: true,
        highlight_titles_in_summary: true,
        key_events: keyEventIds,
    }
    const response = await api.post("/v6/messages/search", body, {
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    const fetchedSummary = response.data as MessagesSummary
    return {
        summary: fetchedSummary?.executive_summary ?? "",
        messages: fetchedSummary?.messages ?? [],
        highlights: fetchedSummary?.executive_summary_highlights ?? [],
    }
}

async function fetchPreGeneratedSummary(titleId: number): Promise<TitleSummary> {
    const queryParams = {
        stream: "ai-executive-summaries",
        language: "en",
        format: "json",
        highlight_titles_in_text: true,
        plaintext: true,
    }
    const response = await api.get(`/v6/newsbot/titles/${titleId}`, {
        params: queryParams,
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })

    const summary: string = response.data?.rss?.channel?.item[0]?.description["%"]
    const publishDateString: string = response.data?.rss?.channel?.item[0]?.pubDate
    const highlights: TitleHighlight[] = response.data?.rss?.channel?.item[0]?.text_highlights || []

    if (summary === undefined) {
        return { summary: "", messages: [], highlights: [] }
    }

    const publishDate = publishDateString ? new Date(publishDateString).getTime() : undefined

    return { summary, messages: [], highlights, publishDate }
}

async function fetchChartData(
    source: string,
    startDate: Date,
    endDate: Date,
    titleIds: number[],
    isExtendedTimeRange = true
): Promise<HistoryChartData> {
    const reportModel = getReportModel(source, startDate, endDate, isExtendedTimeRange)

    const postBody = {
        datasets: [
            {
                feature: "Buzz/Sentiment",
                fields: [
                    {
                        field: "buzz",
                        name: "Buzz",
                    },
                    {
                        field: "sentiment",
                        name: "Sentiment",
                    },
                ],
                item: "Buzz Reports",
                report_model: reportModel.id,
                titles: titleIds,
                type: "column",
            },
        ],
        filter: [
            {
                comparator: "eq",
                field: "id",
                value: titleIds,
            },
        ],
        identifiers: ["id"],
    }

    const queryParams = {
        preview: false,
        startDate,
        endDate,
    }

    try {
        const response = await api.post("/v6/datasets/history", postBody, { params: queryParams })

        return { ...response.data, reportModel }
    } catch (ignored) {
        return { history: [], reportModel }
    }
}

async function fetchQuoteChartData(
    source: string,
    startDate: Date,
    endDate: Date,
    titleIds: number[],
    isExtendedTimeRange = true
): Promise<{
    history: QuotesForTitlesQuotesInner[]
    reportModel: ReportModel
}> {
    const reportModel = getReportModel(source, startDate, endDate, isExtendedTimeRange)

    if (reportModel.type === ReportModelType.DAILY) {
        return { ...(await fetchDailyQuoteChartData(startDate, endDate, titleIds)), reportModel }
    }

    return { ...(await fetchIntradayQuoteChartData(startDate, endDate, titleIds)), reportModel }
}

async function fetchIntradayQuoteChartData(
    startDate: Date,
    endDate: Date,
    titleIds: number[]
): Promise<{
    history: QuotesForTitlesQuotesInner[]
}> {
    const queryParams = {
        startDate,
        endDate,
        partition: DEFAULT_REPORT_MODELS.realtime.name,
        data_options: "ohlcv",
    }

    const response = await api.get(`/v6/titles/${titleIds.join(",")}/intraday`, { params: queryParams })

    return { history: response.data.quotes }
}

async function fetchDailyQuoteChartData(
    startDate: Date,
    endDate: Date,
    titleIds: number[]
): Promise<{ history: QuotesForTitlesQuotesInner[] }> {
    const postBody = {
        datasets: [
            {
                feature: "Price Data",
                fields: [
                    { field: "open", name: "o" },
                    { field: "high", name: "h" },
                    { field: "low", name: "l" },
                    { field: "close", name: "c" },
                    { field: "volume", name: "v" },
                ],
                item: "Daily Quotes",
                report_model: DEFAULT_REPORT_MODELS.daily.id,
                titles: titleIds,
            },
        ],
        filter: [
            {
                comparator: "eq",
                field: "id",
                value: titleIds,
            },
        ],
        identifiers: ["id"],
    }

    const queryParams = { preview: false, startDate, endDate }

    const response = await api.post("/v6/datasets/history", postBody, { params: queryParams })
    return response.data
}

function getCoreToUse(startDate: Date): string {
    return differenceInMilliseconds(new Date(), startDate) < 24 * 60 * 60 * 1000
        ? MessagesCore.Messages24h
        : MessagesCore.Messages
}

export interface ReferencedPersonLabel {
    label: string
    value: number
    id: number
}

export interface ReferencedPersons {
    statistics: ReferencedPersonLabel[]
    entities: PersonV6[]
}

async function extractReferencedPersons(
    top10Entities: SearchMessagesStatisticsTop10Entities
): Promise<ReferencedPersons> {
    const entityIds = Object.entries(top10Entities).map((value) => parseInt(value[0]))
    const entities = await fetchEntities(entityIds)

    const statistics = Object.entries(top10Entities).map((value) => {
        return {
            label: getEntityName(parseInt(value[0]), entities),
            value: value[1] as number,
            id: parseInt(value[0]),
        }
    })
    return {
        statistics,
        entities,
    }
}

async function fetchEntities(entityIds: number[]): Promise<PersonV6[]> {
    if (entityIds.length === 0) {
        return []
    }
    const response = await api.get(`/v6/entities/${entityIds.join(",")}?format=json`)
    return response.data
}

async function updateUserOpts(
    currentUserOpts: AsyncData<UserOpts>,
    uiPreferences: UiPreferences
): Promise<UserOpts | undefined> {
    const newUiPreferences = {
        ...currentUserOpts.value?.uiPreferences,
        markets: uiPreferences.markets,
        titleTypes: uiPreferences.titleTypes,
    }
    const newUserOpts = {
        ...currentUserOpts.value,
        uiPreferences: newUiPreferences,
    }

    const body = { opts: JSON.stringify(newUserOpts) }
    const response = await api.post("/v6/userdata/opts", body, { cancellationPolicy: CancellationPolicy.OnLogout })
    if (response.status === 200) {
        return newUserOpts
    }
    return undefined
}

function getEntityName(entityId: number, entities: Array<{ entity_id: number; n: string }>): string {
    return entities?.filter((entity) => entity.entity_id === entityId).at(0)?.n || ""
}

async function fetchTitleQuotes(
    titleId: number,
    startDate: Date,
    endDate: Date,
    cancellationPolicy?: CancellationPolicy
): Promise<QuotesForTitlesQuotesInner[]> {
    const response = await api.get(`/v6/titles/${titleId}/quotes`, {
        params: {
            startDate,
            endDate,
        },
        cancellationPolicy,
    })

    return response.data.quotes
}

async function fetchTitleChartData(
    title: Title,
    startDate: Date,
    endDate: Date,
    cancellationPolicy?: CancellationPolicy
): Promise<TitleChartData> {
    const response = await api.get(`/v6/titles/${title.id}/quotes`, {
        params: {
            startDate,
            endDate,
        },
        cancellationPolicy,
    })
    const quotes: QuotesForTitlesQuotesInner[] = response.data.quotes

    const clearedQuotes: Array<
        QuotesForTitlesQuotesInner & {
            t: number
            c: number
        }
    > = quotes.filter((quote) => quote.t !== undefined && quote.c !== undefined) as Array<
        QuotesForTitlesQuotesInner & {
            t: number
            c: number
        }
    >
    clearedQuotes.sort((a, b) => {
        return a.t - b.t
    })

    const dataSeries: Array<Array<number | string>> = clearedQuotes.map((quote) => [
        DateFormatter.formatDate(quote.t * 1000, "YYYY-MM-DD"),
        quote.c,
    ])

    const minDataValue = Math.min(...dataSeries.map((dataPoint) => dataPoint[1] as number))
    const maxDataValue = Math.max(...dataSeries.map((dataPoint) => dataPoint[1] as number))

    let rounding = 1000
    while (maxDataValue - minDataValue < rounding) {
        rounding /= 10
    }

    return {
        titleId: title.id,
        titleName: title.name,
        chartDataSeries: dataSeries,
        minValue: Math.floor(minDataValue / rounding) * rounding,
        maxValue: Math.ceil(maxDataValue / rounding) * rounding,
        lastQuote: clearedQuotes.slice(-1)[0],
        secondLastQuote: clearedQuotes.slice(-2)[0],
        currency: title.currency || "",
    }
}

async function filterTitles(
    query: FilterSettingsList,
    limit: number,
    sortBy: string,
    partition?: string,
    cancellationPolicy?: CancellationPolicy
): Promise<TitleFromFilter[]> {
    const params = {
        limit,
        sort_by: sortBy,
        partition,
    }
    const response = await api.post("/v6/titles", query, {
        params,
        headers: {
            "Content-Type": "application/json",
        },
        cancellationPolicy,
    })
    return response.data
}

export type EbsTickEvent = StandardTriggersInner & {
    release_date: string
    consensus: number
    prior: number | null
    actual: number | null
    eps: number | null
}
export type EarningsReleaseTickEvent = StandardTriggersInner & {
    release_date: string
    prior: number | null
    actual: number | null
}

export type SignalObject = StandardTriggersInner | EbsTickEvent | EarningsReleaseTickEvent

export function isEarningsReleaseTickEvent(event: SignalObject): event is EarningsReleaseTickEvent {
    return "release_date" in event && ("prior" in event || "actual" in event)
}

async function fetchStandardTriggers(
    titleId: number,
    startDate: Date,
    endDate: Date,
    limit: number = 20,
    ruleTypes: string[]
): Promise<SignalObject[]> {
    const response = await api.get(`/v6/standard_triggers/${titleId}`, {
        params: { startDate, endDate, limit, rule_types: ruleTypes },
    })
    return response.data as SignalObject[]
}

async function fetchEarnings(titleId: number, startDate: Date, endDate: Date): Promise<EarningsTitlesInner[]> {
    const response = await api.get(`/v6/titles/${titleId}/earnings`, {
        params: { startDate, endDate },
    })
    return response.data as EarningsTitlesInner[]
}

async function searchEntities(query: string, entityType?: string, limit?: number): Promise<SolrEntitiesSearchResult> {
    const params = {
        query,
        entity_type: entityType,
        limit,
    }
    const response = await api.get("/v6/entities/search", { params })
    return response.data
}

async function searchAuthors(query: string, limit?: number): Promise<SolrEntitiesSearchResult> {
    const params = {
        query,
        limit,
    }
    const response = await api.get("/v6/authors/search", { params })
    return response.data
}

export interface Executive {
    firstName?: string
    middleName?: string
    lastName: string
    name: string
    city?: string
    birthdate?: string
    role: string
    entryDate: string
}

async function fetchExecutives(titleId: number): Promise<Executive[]> {
    const response = await api.get(`/v6/titles/${titleId}/positions`, {
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    const executives = (response.data?.company_register ?? []) as Array<Omit<Executive, "name">>

    return executives.map((executive) => {
        let name = executive.lastName

        if (executive.middleName || executive.firstName) {
            name += ", " + [executive.firstName, executive.middleName].filter((item) => item !== undefined).join(" ")
        }
        return { ...executive, name }
    })
}

export interface Subsidiary {
    subsidiaryName: string
    share: number
}

export interface SubsidiaryCollection {
    year: number
    subsidiaries: Subsidiary[]
}

async function fetchSubsidiaries(titleId: number): Promise<SubsidiaryCollection> {
    const response = await api.get(`/v6/titles/${titleId}/subsidiaries`, {
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return (response.data?.federal_gazette ?? {}) as SubsidiaryCollection
}

/**
 * Replaces the domain of a given URL with a new one.
 *
 * @param url - The original URL.
 * @param newDomain - The new domain (e.g. "https://new.com").
 * @returns The URL with the domain replaced.
 */
function switchDomain(url: string, newDomain: string): string {
    try {
        const parsed = new URL(url)
        const newBase = new URL(newDomain)

        // Construct a new URL with the same path, query, and hash
        return `${newBase.origin}${parsed.pathname}${parsed.search}${parsed.hash}`
    } catch (error) {
        console.error("Invalid URL provided:", error)
        return url
    }
}

async function fetchStreamPosts(
    region: string[],
    titleTypes: string[],
    types: string[],
    limit: number,
    createdBefore?: string,
    addGlobals = true,
    filterByWatchlist = false,
    titleIds: number[] = [],
    cancellationPolicy?: CancellationPolicy
): Promise<StreamPost[]> {
    let streamPostDate
    if (createdBefore) {
        streamPostDate = `{* TO ${createdBefore}]`
    }

    const response = await api.get("/v6/stream_posts", {
        params: {
            region: region.join(","),
            titleTypes: titleTypes.length ? titleTypes.join(",") : undefined,
            limit,
            type: types.length ? types.join(",") : undefined,
            streamPostDate,
            addGlobals,
            watchlistOnly: filterByWatchlist,
            titleIds: titleIds.length ? titleIds.join(",") : undefined,
        },
        cancellationPolicy,
    })
    const data = response.data as StreamPost[]

    return data.map((post) => ({
        ...post,
        image_url: post.image_url ? switchDomain(post.image_url, IMAGE_PROXY_DOMAIN) : null,
    }))
}

async function fetchStreamPostsByTitles(
    titleIds: number[],
    limit: number,
    createdBefore?: string
): Promise<StreamPost[]> {
    return fetchStreamPosts(
        [],
        [],
        [],
        limit,
        createdBefore,
        false,
        false,
        titleIds,
        CancellationPolicy.OnContextSwitch
    )
}

async function fetchStreamPost(postUUID: string): Promise<StreamPost> {
    const streamPosts = await fetchStreamPostsByPostUUIDs([postUUID])
    return streamPosts[0]
}

async function fetchStreamPostsByPostUUIDs(postUUIDs: string[]): Promise<StreamPost[]> {
    const response = await api.get(`/v6/stream_posts/${postUUIDs.join(",")}`, {
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    const data = response.data as StreamPost[]

    return data.map((post) => ({
        ...post,
        image_url: post.image_url ? switchDomain(post.image_url, IMAGE_PROXY_DOMAIN) : null,
    }))
}

export interface TrendRadarTopStoriesParams {
    startDate: Date
    endDate: Date
    topN?: number
    offset?: number
    countries: string[]
    categories: string[]
}

export interface TrendingTopicsParams {
    startDate: Date
    endDate: Date
    topN?: number
    offset?: number
    source: string | string[]
}

export type Article = {
    globalClusterId: number
    clusterId: number
    articleId: number
    headline: string
    sourceDomain: string
    publishedTime: string
    sourceCountry: string
    targetLink: string
    inferredCategories: string[]
    relatedEntities: Array<Record<string, unknown>>
    otherCategories: string[]
    topTerms: string[]
    quotes: Array<Record<string, unknown>>
}

export type StorySummaryRaw = {
    globalClusterId: number
    storySize: number
    medoidArticleId: number
    samples: Article[]
    series: Array<[number, number]>
}

async function fetchTrendRadarTopStories(
    searchCompletionParams: TrendRadarTopStoriesParams
): Promise<StorySummaryRaw[]> {
    const response = await api.get("/v6/trend_radar/top_stories", {
        params: searchCompletionParams,
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as StorySummaryRaw[]
}

export interface StorySamplesRequestBody {
    globalClusterId: number
    cursorArticleId?: number
    pageSize: number
    countries: string[]
    categories: string[]
}

async function fetchTrendRadarStorySamples(storyParams: StorySamplesRequestBody): Promise<Article[]> {
    const response = await api.get("/v6/trend_radar/story_samples", {
        params: storyParams,
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as Article[]
}

export type TopicClusterSeries = {
    global_cluster_id: number
    medoid_topic_id: number
    cluster_labels: string[]
    series: Array<[number, number]>
    cumulative_count: number
}

async function fetchTrendRadarTrendingTopics(
    trendingTopicsParams: TrendingTopicsParams
): Promise<TopicClusterSeries[]> {
    const response = await api.get("/v6/trend_radar/trending_topics", {
        params: trendingTopicsParams,
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as TopicClusterSeries[]
}

export type ArticleSource = {
    sourceDomain: string
    sourceCountry: string
    articleCount: number
}

export interface TopSourcesParams {
    startDate: Date
    endDate: Date
    countries: string[]
    categories: string[]
    limit: number
}

async function fetchTrendRadarTopSources(topSourceParams: TopSourcesParams): Promise<ArticleSource[]> {
    const response = await api.get("/v6/trend_radar/top_sources", {
        params: topSourceParams,
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as ArticleSource[]
}

export type CategoryCount = {
    category: string
    articleCount: number
}

async function fetchTrendRadarTopCategories(topSourceParams: TopSourcesParams): Promise<CategoryCount[]> {
    const response = await api.get("/v6/trend_radar/top_categories", {
        params: topSourceParams,
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as CategoryCount[]
}

export interface SearchCompletionSeries {
    data: Array<[string, number]> // ISO 8601 timestamp as a string
    metaName?: string | null
    metaType?: string | null
    searchString: string
}

export interface SearchCompletionParams {
    startDate: Date
    endDate: Date
    bucketSizeHours?: number
}

export interface SearchCompletionRequestBody {
    userTerms: string[]
    topN: number
    bucketSize: number
}

async function fetchGoogleTrendsTopSearchCompletions(
    searchCompletionParams: SearchCompletionParams,
    searchCompletionBody: SearchCompletionRequestBody
): Promise<SearchCompletionSeries[]> {
    const response = await api.post("/v6/google_search_completions/top", searchCompletionBody, {
        params: searchCompletionParams,
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as SearchCompletionSeries[]
}

export interface SearchTermResult {
    searchString: string
    metaName?: string
    metaType?: string
    firstSeen: string // ISO 8601 date-time string
    lastSeen: string // ISO 8601 date-time string
    totalCount: number
    relevance: number
}

async function fetchGoogleTrendsSearchTerms(term: string, limit: number): Promise<SearchTermResult[]> {
    const response = await api.get("/v6/google_search_completions/search", {
        params: {
            limit,
            term,
        },
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as SearchTermResult[]
}

export interface ResearchRequest {
    requestId: number
    username: string
    requestElements: string // JSON string stored in the DB.
    result: string | null
    status: "pending" | "processing" | "completed" | "failed" | "disabled"
    scheduledTime: Date | null
    repeatInterval: number | null
    disabled: number
    priority: number
    createdAt: Date
    updatedAt: Date
    nextRunAt: string
}

async function fetchResearchRequests(offset?: number, limit?: number): Promise<ResearchRequest[]> {
    const response = await api.get("/v6/research/request", {
        params: {
            limit,
            offset,
        },
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as ResearchRequest[]
}

async function createSearchRequest(
    searchTerms: string[],
    scheduledTime?: string,
    repeatInterval?: number
): Promise<ResearchRequest> {
    const response = await api.post(
        "/v6/research/request",
        {
            requestElements: searchTerms,
            scheduledTime,
            repeatInterval,
        },
        {
            cancellationPolicy: CancellationPolicy.OnContextSwitch,
        }
    )
    return response.data as ResearchRequest
}

async function deleteSearchRequest(requestId: number): Promise<boolean> {
    const response = await api.delete(`v6/research/request/${requestId}`)
    return response.status === 200
}

export interface PatchResearchRequestBody {
    disabled?: number
}

async function patchSearchRequest(requestId: number, patchBody: PatchResearchRequestBody): Promise<boolean> {
    const response = await api.patch(`v6/research/request/${requestId}`, patchBody)
    return response.status === 202
}

async function loadTiktokThumbnail(videoUrl: string): Promise<string> {
    const postBody = {
        url: videoUrl,
    }
    const response = await api.post("/v6/tiktok/thumbnail", postBody)

    return response.data
}

export type SearchResult = {
    queryId: number
    query: string
    url: string
    matchId: number
    name: string
    occupation: string
    entityType: string
    model: string
    score: number
    updatedAt: number
}

export type ResearchRequestWithResult = ResearchRequest & {
    results: SearchResult[] | null
}

async function getSearchRequest(requestId: number): Promise<ResearchRequestWithResult> {
    const response = await api.get(`v6/research/request/${requestId}`, {
        cancellationPolicy: CancellationPolicy.OnContextSwitch,
    })
    return response.data as ResearchRequestWithResult
}

async function getBacktestResult(
    signalGroupId: number,
    policies: PolicySettings,
    signals: SignalInput[] = []
): Promise<BacktestResult> {
    policies.openOrders.forEach((openOrderTemplate: SignalOrderTemplate) => {
        if (
            openOrderTemplate.traded_instrument_type === "OPTION" &&
            isInstanceOfOptionTemplate(openOrderTemplate.traded_instrument)
        ) {
            openOrderTemplate.traded_instrument.exercise_style = policies.useRealOptionPrices ? "AMERICAN" : "EUROPEAN"
        }
    })

    // Prepare the payload using current UI values
    const payload: BacktestRequest = {
        signal_group_id: signalGroupId,
        policies: {
            action_on_new_signal: policies.actionOnNewSignal,
            allocation_option: policies.allocationOption,
            allocation_factor: policies.allocationFactor,
            amount: policies.amount,
            close_before_time_gaps: policies.closeBeforeTimeGaps,
            cost: policies.cost,
            ignore_signals_from_different_date: policies.ignoreSignalsFromDifferentDate,
            overnight_holding: policies.overnightHolding,
            period_ms: policies.periodMs,
            period_profit_goal: policies.periodProfitGoal,
            period_stop_loss: policies.periodStopLoss,
            rolling_period_checks: policies.rollingPeriodChecks,
            striking_method: policies.strikingMethod,
            trading_hours_start: policies.tradingHoursStart,
            trading_hours_stop: policies.tradingHoursStop,
            use_daily_quotes: policies.useDailyQuotes,
            use_real_option_prices: policies.useRealOptionPrices,
            fixed_amount_new_positions: policies.fixedAmountNewPositions,
            risk_free_rate: policies.riskFreeRate,
            start_date: policies.startDate,
        },
        tz_name: policies.timezoneName,
        open_orders: policies.openOrders,
        tracking_orders: policies.trackingOrders,
        signals,
    }

    if (policies.openOrders.reduce((result, order) => result || order.traded_instrument_type === "OPTION", false)) {
        payload.strike_price_intervals = policies.strikePriceIntervals.map((interval: StrikePriceInterval) => {
            return {
                lower_bound_underlying_price: interval.lowerBoundUnderlyingPrice,
                upper_bound_underlying_price: interval.upperBoundUnderlyingPrice,
                price_interval: interval.priceInterval,
            }
        })
    }

    const response = await api.post("https://api.stockpulse.de/v6/backtest", payload)

    return parseLegacyBacktestResult(response.data)
}

function parseLegacyBacktestResult(rawBacktestResult: BacktestResult): BacktestResult {
    if (isNewBacktestResult(rawBacktestResult)) {
        return rawBacktestResult
    }

    // Otherwise convert old structure → new structure
    const converted: BacktestResult = {
        ...rawBacktestResult,
        result: {},
    }

    for (const timestamp of Object.keys(rawBacktestResult.result)) {
        if (timestamp === "included_titles") {
            converted.result[timestamp] = rawBacktestResult.result[timestamp]
            continue
        }
        const period = rawBacktestResult.result[timestamp]
        const newTrades: { [signalId: string]: Trade[] } = {}

        for (const signalIdStr of Object.keys(period.trades)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const oldTrade = (period.trades as any)[signalIdStr] // old Trade

            // Convert old trade into new format Trade[]
            const newTrade: Trade = {
                ...oldTrade,

                // add identifier
                identifier: `${signalIdStr}_${oldTrade.open_order.title_id}_UNDERLYING_ASSET`,

                // add traded instrument
                traded_instrument: {
                    underlying_title_id: String(oldTrade.open_order.title_id),
                    type: "UNDERLYING_ASSET",
                },

                // ensure array shape for included_signals
                included_signals: oldTrade.included_signals ?? [signalIdStr],

                // ensure included_dividends exists
                included_dividends: oldTrade.included_dividends ?? null,
            }

            newTrades[signalIdStr] = [newTrade] // converted to array
        }

        converted.result[timestamp] = {
            ...period,
            trades: newTrades,
        }
    }

    return converted
}

function isNewBacktestResult(result: BacktestResult): boolean {
    const sampleTimestamp = Object.keys(result.result)[0]
    if (!sampleTimestamp) {
        return false
    }

    const samplePeriod = result.result[Number(sampleTimestamp)]
    if (!samplePeriod?.trades) {
        return false
    }

    const firstSignalId = Object.keys(samplePeriod.trades)[0]
    if (!firstSignalId) {
        return false
    }

    const value = samplePeriod.trades[Number(firstSignalId)]

    // New structure → Trade[]
    return Array.isArray(value)
}

async function getBacktestSignals(): Promise<BacktestSignal[]> {
    // Prepare the payload using current UI values
    const response = await api.get("https://api.stockpulse.de/v6/backtest/signals")

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return response.data.map((signal: any) => {
        return {
            strategyName: signal.strategy_name,
            signalGroupId: signal.signal_group_id,
            descEn: signal.desc_en,
            createdAt: signal.created_at,
            rolling_period_checks: signal?.rollingPeriodChecks,
            period_ms: signal?.periodMs,
            period_profit_goal: signal?.periodProfitGoal,
            period_stop_loss: signal?.periodStopLoss,
            overnight_holding: signal?.overnightHolding,
            close_before_time_gaps: signal?.closeBeforeTimeGaps,
            ignore_signals_from_different_date: signal?.ignoreSignalsFromDifferentDate,
            allocation_factor: signal?.allocationFactor,
            amount: signal?.amount,
            cost: signal?.cost,
            striking_method: signal?.strikingMethod,
            action_on_new_signal: signal?.actionOnNewSignal,
            trading_hours_start: signal?.tradingHoursStart,
            trading_hours_stop: signal?.tradingHoursStop,
            timezone_name: signal?.timezoneName,
            use_daily_quotes: signal?.useDailyQuotes,
            stop_order_definitions: signal?.stopOrderDefinitions,
            open_order_definitions: signal?.openOrderDefinitions,
        }
    })
}

export {
    api,
    axiosRequestManager,
    fetchExecutives,
    fetchSubsidiaries,
    fetchTitleChartData,
    fetchTitleQuotes,
    fetchInfosForTitles,
    fetchStatistics,
    fetchTopicsList,
    fetchChartData,
    fetchEarnings,
    fetchSolrMessages,
    fetchQuoteChartData,
    fetchSummary,
    fetchPreGeneratedSummary,
    fetchStandardTriggers,
    filterTitles,
    fetchStreamPosts,
    fetchStreamPost,
    fetchStreamPostsByTitles,
    fetchStreamPostsByPostUUIDs,
    extractReferencedPersons,
    getCoreToUse,
    updateUserOpts,
    searchEntities,
    searchAuthors,
    fetchGoogleTrendsTopSearchCompletions,
    fetchGoogleTrendsSearchTerms,
    createSearchRequest,
    fetchResearchRequests,
    deleteSearchRequest,
    fetchTrendRadarTopStories,
    fetchTrendRadarStorySamples,
    fetchTrendRadarTopSources,
    fetchTrendRadarTopCategories,
    fetchTrendRadarTrendingTopics,
    getSearchRequest,
    patchSearchRequest,
    loadTiktokThumbnail,
    getBacktestResult,
    getBacktestSignals,
    parseLegacyBacktestResult,
}
